export interface IActivity {
  id: string;
  label: string;
  description: string;
}

export interface IMotif {
  id: string;
  label: string;
  description: string;
  recognition: string;
  default_flow: string;
  activities: string[];
}

// Domain.name value identifying the real estate vertical (the only one currently supported).
export const REAL_ESTATE_VERTICAL_NAME = 'Immobilier';

// Fallback used when a Domain has not configured its own activities/motifs yet.
export const FALLBACK_ACTIVITIES: IActivity[] = [
  { id: 'transaction', label: 'Transaction', description: 'Vous vendez et vous faites acheter des biens.' },
  { id: 'rental', label: 'Location', description: 'Vous louez des biens et recevez des candidats locataires.' },
  { id: 'management', label: 'Gestion locative', description: 'Vous gérez des biens et suivez les locataires en place.' },
  { id: 'commercial', label: 'Immobilier professionnel', description: 'Vous traitez des locaux commerciaux, bureaux, entrepôts ou fonds de commerce.' },
  { id: 'recruitment', label: 'Recrutement', description: "Vous recevez des candidatures pour rejoindre l'agence." },
];

export const FALLBACK_MOTIFS: IMotif[] = [
  {
    id: 'buy',
    label: 'Acheter un bien',
    description: 'Un acheteur cherche un bien à acheter, pour y vivre ou pour investir.',
    recognition: "l'appelant cherche un bien à acheter, pour y vivre ou pour investir.",
    default_flow: "Comprends ce qu'il cherche : le type de bien, le secteur, et son budget même approximatif. Demande-lui s'il s'agit d'un achat pour y vivre ou d'un investissement. Ne parle jamais d'un bien précis, d'un prix ou d'une disponibilité.",
    activities: ['transaction'],
  },
  {
    id: 'sell',
    label: 'Vendre ou faire estimer',
    description: 'Un propriétaire veut vendre, ou juste savoir ce que vaut son bien.',
    recognition: "l'appelant possède un bien qu'il souhaite vendre ou faire estimer.",
    default_flow: "Comprends de quel bien il s'agit : où il se trouve, le type et la surface à peu près. Demande s'il souhaite vendre ou seulement connaître la valeur du bien, et dans quel délai. Ne donne jamais de prix, de fourchette ni d'estimation, même approximative, même s'il insiste.",
    activities: ['transaction'],
  },
  {
    id: 'rent_search',
    label: 'Louer ou déposer un dossier',
    description: 'Quelqu\'un cherche une location ou veut candidater sur une annonce.',
    recognition: "l'appelant cherche une location ou souhaite candidater sur une annonce.",
    default_flow: "Demande d'abord s'il appelle pour une annonce précise ou pour une recherche plus large. Comprends ce qu'il cherche, dans quel secteur, son budget de loyer et à partir de quand il lui faut le logement. Ne dis jamais si un logement est encore disponible et ne commente jamais l'avancement d'un dossier.",
    activities: ['rental'],
  },
  {
    id: 'tenant_in_place',
    label: 'Locataire en place',
    description: 'Un locataire appelle pour son logement : panne, préavis, quittance, charges.',
    recognition: "l'appelant est locataire d'un bien géré par l'agence et appelle au sujet de son logement : panne, préavis, quittance, charges, ou autre.",
    default_flow: "Demande l'adresse du logement et de quoi il s'agit exactement : une panne, un préavis, une quittance, les charges ou autre chose. Demande si c'est urgent ou si cela peut attendre. Ne donne jamais d'information sur son bail, son compte, ses charges ou l'avancement de sa demande, et ne confirme jamais qu'il est bien locataire chez vous.",
    activities: ['management'],
  },
  {
    id: 'landlord_mandate',
    label: 'Confier un bien en location ou en gestion',
    description: 'Un propriétaire veut mettre son bien en location, ou en confier la gestion.',
    recognition: "l'appelant est propriétaire d'un bien et souhaite le mettre en location ou en confier la gestion à l'agence.",
    default_flow: "Comprends de quel bien il s'agit, où il se trouve et à partir de quand il est libre. Demande s'il souhaite seulement trouver un locataire ou confier la gestion complète à l'agence, et si la location serait vide ou meublée. Ne donne jamais de montant de loyer, d'honoraires ni d'estimation.",
    activities: ['rental', 'management'],
  },
  {
    id: 'commercial',
    label: 'Projet immobilier professionnel',
    description: 'Local commercial, bureaux, entrepôt ou fonds de commerce, à la vente ou à la location.',
    recognition: "l'appelant cherche ou propose un local commercial, des bureaux, un entrepôt ou un fonds de commerce, à l'achat, à la vente ou à la location.",
    default_flow: "Demande d'abord s'il cherche un local ou s'il en propose un. Comprends le type de local, l'activité prévue, le secteur et la surface à peu près, et s'il s'agit d'un achat ou d'une location. Ne parle jamais de prix, de loyer, de droit au bail, de pas-de-porte ni de disponibilité.",
    activities: ['commercial'],
  },
  {
    id: 'recruitment',
    label: "Rejoindre l'agence",
    description: 'Candidature spontanée, réponse à une annonce, stage ou alternance.',
    recognition: "l'appelant candidate à un poste, spontanément ou en réponse à une annonce, pour un stage, une alternance ou un poste salarié.",
    default_flow: "Demande quel poste l'intéresse. Explique qu'il peut envoyer son CV par mail à l'agence en précisant ce poste, et que les candidatures sont regardées à réception. Ne dis jamais si un poste est ouvert ou fermé, ne parle jamais de rémunération et ne commente aucune candidature.",
    activities: ['recruitment'],
  },
  {
    id: 'partner',
    label: 'Professionnel sur un dossier',
    description: 'Notaire, diagnostiqueur, syndic, artisan ou confrère qui appelle au sujet d\'un dossier.',
    recognition: "l'appelant est un notaire, un diagnostiqueur, un syndic, un artisan ou un confrère qui appelle au sujet d'un dossier ou d'un bien.",
    default_flow: "Demande qui il est et pour quelle société ou quelle étude, puis le dossier ou le bien concerné et ce qu'il attend de l'agence. Ne confirme jamais qu'un dossier ou un bien existe et ne donne aucune information dessus. Le numéro entrant sert au rappel, ne le redemande pas.",
    activities: [],
  },
];
